import { useBrowserStore } from "@/composables/useBrowserStore";
import { in_electron } from "@/utils/in";
import type {
  Peripheral,
  PeripheralPermissionStatus,
} from "@/utils/peripherals";

/**
 * Speaker peripheral (audio output)
 */
export type SpeakerDevice = Peripheral;

const STORAGE_KEY = "audio/speaker:id";

export const speaker = {
  /**
   * Check if speaker output selection is supported
   *
   * - Electron: Always returns "granted" (speaker access is always available)
   * - Web browsers: Checks if setSinkId() is supported for audio output selection
   *
   * Note: Speakers don't have their own permission in the browser Permissions API.
   * The ability to enumerate speakers with labels depends on having microphone permission.
   */
  getPermission: async (): Promise<PeripheralPermissionStatus> => {
    // Electron: Speaker access is always available
    if (in_electron) {
      return "granted";
    }

    if (typeof HTMLMediaElement === "undefined") {
      return "unsupported";
    }

    // Check if setSinkId is supported (ability to select audio output device)
    const audio = document.createElement("audio");
    if (typeof audio.setSinkId !== "function") {
      return "unsupported";
    }

    // setSinkId is supported, speaker selection is available
    return "granted";
  },

  /**
   * Enumerate all available speaker (audio output) peripherals
   *
   * Note: Peripheral labels may be empty if speaker permissions haven't been granted
   * (browsers require speaker access to expose peripheral labels).
   */
  enumerateDevices: async (): Promise<SpeakerDevice[]> => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`,
          kind: d.kind,
          groupId: d.groupId,
        }));
    } catch (error) {
      window.console.warn("Failed to enumerate speaker devices:", error);
      return [];
    }
  },

  /**
   * Get the stored selected speaker device ID from localStorage,
   * account-namespaced (adopting the value written un-namespaced by older
   * versions on first read). Raw format, like the legacy value.
   */
  getSelectedDeviceId: (accountId: string): string | null => {
    try {
      const store = useBrowserStore(accountId);
      store.migrate(STORAGE_KEY);
      return store.getRaw(STORAGE_KEY, "") || null;
    } catch {
      return null;
    }
  },

  /**
   * Save the selected speaker device ID to localStorage, account-namespaced
   */
  setSelectedDeviceId: (accountId: string, deviceId: string | null): void => {
    try {
      const store = useBrowserStore(accountId);
      if (deviceId) {
        store.setRaw(STORAGE_KEY, deviceId);
      } else {
        store.unset(STORAGE_KEY);
      }
    } catch (error) {
      window.console.warn("Failed to save speaker device preference:", error);
    }
  },
};
